import { Injectable } from '@angular/core';
import { catchError, map, Observable, timeout } from "rxjs";
import { HttpClient } from '@angular/common/http';
import { ApiService } from './api.service';
import { GROUP_STRANDS_PUPILS, GroupStrandsPupilsError, SchoolGroup, StrandedPupil } from '@core/models/domain/group.domain';
import { SchoolGroupInfoResponse } from '@core/models/response/group.response';
import { buildApiEndpoint, handleError } from '@core/utils/service';
import { buildSchoolGroupFromResponse } from '@core/converter/group.converter';
import { DigiResponse } from '@core/models/response/core';
import { CreateSchoolGroupRequest, ModifySchoolGroupRequest } from '@core/models/request/group.request';

/**
 * GroupService
 *
 * This service provides methods for managing school groups, including loading, creating, updating, and deleting groups.
 * It facilitates communication with the backend for group-related operations.
 *
 * Key Features:
 * - Loads all groups associated with the school account.
 * - Creates new groups by sending requests to the server.
 * - Updates existing groups by sending modification requests to the server.
 * - Deletes groups by sending delete requests to the server.
 */

@Injectable({
  providedIn: 'root'
})
export class GroupService {
  static readonly LOAD_TIMEOUT_MS = 30_000;

   API_URI: string;

  constructor(private http: HttpClient, private apiService: ApiService) { 
    this.API_URI = this.apiService.getApiURL("GroupService", "school-account");
  }

  /**
   * Loads all groups by sending a GET request to the server.
   *
   * This function retrieves a list of groups from the server by making a GET request to the `get-groups` endpoint.
   * The response is mapped to an array of `SchoolGroup` objects using the `buildSchoolGroupFromResponse` converter.
   * Logs a warning if the response body is invalid or not an array, and returns an empty array in such cases.
   *
   * @returns {Observable<SchoolGroup[]>} An observable that emits an array of `SchoolGroup` objects or an empty array in case of an invalid response.
   */
  loadAllGroups(): Observable<SchoolGroup[]> {
    return this.http
      .get<DigiResponse<SchoolGroupInfoResponse[]>>(buildApiEndpoint(this.API_URI, "get-groups"), { observe: 'response' })
      .pipe(
        // Fail fast instead of hanging until the proxy's ~60s 504 when the backend is slow.
        timeout(GroupService.LOAD_TIMEOUT_MS),
        map((response) => {
          const loqtResponse = response.body!;
          if (!loqtResponse.body || !Array.isArray(loqtResponse.body)) {
            console.warn("Invalid groups response", loqtResponse.body);
            return [];
          }
          console.log("Groups loaded successfully:", loqtResponse.body);
          return loqtResponse.body.map(buildSchoolGroupFromResponse);
        }),
        catchError((error) => handleError<SchoolGroup[]>("Loading groups", error))
      );
  }

  /**
   * Creates a new group by sending a POST request to the server.
   *
   * This function sends a POST request to the `create-group` endpoint with the provided group creation data.
   * If the operation is successful, the response is mapped to a `SchoolGroup` object.
   * Logs the added group details and throws an error if no data is found in the response.
   *
   * @param {CreateSchoolGroupRequest} request - The request object containing the group creation data.
   * @returns {Observable<SchoolGroup>} An observable that emits the created `SchoolGroup` object or throws an error if the operation fails.
   */
  createGroup(request: CreateSchoolGroupRequest): Observable<SchoolGroup> {
    return this.http
      .post<DigiResponse<SchoolGroupInfoResponse>>(buildApiEndpoint(this.API_URI, "add-group"), request, { observe: 'response' })
      .pipe(
        map((response) => {
          const loqtResponse = response.body!;
          const isSuccess = loqtResponse.statusCode.status === "OK" || loqtResponse.statusCode.status === "CREATED";
          if (isSuccess && loqtResponse.body) {
            console.log("Group added successfully:", loqtResponse.body);
            return buildSchoolGroupFromResponse(loqtResponse.body);
          }
          const reason = loqtResponse.statusCode.reason || "No group data found in response";
          throw new Error(reason);
        }),
        catchError((error) => handleError<SchoolGroup>("Adding group", error))
      );
  }

  /**
   * Updates an existing group by sending a PUT request to the server.
   *
   * This function sends a PUT request to the `modify-group` endpoint with the provided group modification data.
   * If the operation is successful, the response is mapped to a `SchoolGroup` object.
   * Logs the updated group details and throws an error if no data is found in the response.
   *
   * @param {ModifySchoolGroupRequest} request - The request object containing the group modification data.
   * @returns {Observable<SchoolGroup | null>} An observable that emits the updated `SchoolGroup` object or throws an error if the operation fails.
   */
  updateGroup(request: ModifySchoolGroupRequest): Observable<SchoolGroup | null> {
    return this.http
      .put<DigiResponse<SchoolGroupInfoResponse>>(buildApiEndpoint(this.API_URI, "modify-group"), request, { observe: 'response' })
      .pipe(
        map((response) => {
          const loqtResponse = response.body!;
          if (loqtResponse.statusCode.status === "OK" && loqtResponse.body) {
            console.log("Group updated successfully:", loqtResponse.body);
            return buildSchoolGroupFromResponse(loqtResponse.body);
          }
          throw new Error("No group data found in response");
        }),
        catchError((error) => handleError<SchoolGroup>("Updating group", error))
      );
  }

  /**
   * Deletes a group by sending a DELETE request to the server.
   *
   * This function sends a DELETE request to the `remove-group` endpoint with the specified group ID.
   * If the operation is successful, the response is mapped to the group ID.
   * Logs the removed group ID and throws an error if no data is found in the response.
   *
   * @param {string} groupId - The ID of the group to be removed.
   * @param {boolean} acknowledgeStrandedPupils - Confirms the removal even if it leaves pupils in no active group.
   * @returns {Observable<string>} An observable that emits the removed group ID or throws an error if the operation fails.
   * When the removal would leave pupils in no active group and was not acknowledged, it throws a
   * {@link GroupStrandsPupilsError} naming them.
   */
  deleteGroup(groupId: string, acknowledgeStrandedPupils = false): Observable<string> {
    const endpoint = `remove-group/${groupId}` + (acknowledgeStrandedPupils ? '?acknowledgeStrandedPupils=true' : '');
    return this.http
      .delete<DigiResponse<any>>(buildApiEndpoint(this.API_URI, endpoint), { observe: 'response' })
      .pipe(
        map((response) => {
          const loqtResponse = response.body!;
          if (loqtResponse.statusCode.reason === GROUP_STRANDS_PUPILS) {
            throw new GroupStrandsPupilsError(groupId, (loqtResponse.body ?? []) as StrandedPupil[]);
          }
          if (loqtResponse.statusCode.status === "OK" && loqtResponse.body) {
            console.log("Group removed successfully:", loqtResponse.body);
            return loqtResponse.body;
          }
          throw new Error("No user data found in response");
        }),
        catchError((error) => handleError<string>("Removing group", error))
      );
  }

}